import moment from 'moment-timezone'

import { formatHotelTime } from './formatHotelTime'

export const PEP_DATA_RHYTHM_MODES = {
  onboarding: 'onboarding',
  ongoing: 'ongoing'
}

export const PEP_DATA_RHYTHM_STATUS = {
  normal: 'normal',
  waiting: 'waiting',
  pullOverdue: 'pullOverdue',
  noAppSignal: 'noAppSignal',
  noPullYet: 'noPullYet'
}

const ONBOARDING_PULL_INTERVAL_MINUTES = 18
const ONGOING_PULL_INTERVAL_MINUTES = 120
const HEARTBEAT_INTERVAL_MINUTES = 5
const NO_APP_SIGNAL_MINUTES = 24 * 60
const RECENT_APP_SIGNAL_MINUTES = 60

function findLatestDataPullEvent(timelineEvents) {
  let latest = null

  for (const ev of timelineEvents || []) {
    const type = (ev.eventType ?? ev.EventType ?? '').toString().toLowerCase()
    const code = (ev.eventCode ?? ev.EventCode ?? '').toString()
    const isPullSuccess =
      type.includes('datapullsucceeded') ||
      type.includes('data_pull_succeeded') ||
      code === 'PEP_DATA_PULL_OK'

    if (!isPullSuccess) {
      continue
    }

    const occurredAt = ev.occurredAt ?? ev.OccurredAt
    const t = moment.utc(occurredAt)
    if (!t.isValid()) {
      continue
    }

    if (!latest || t.isAfter(moment.utc(latest.occurredAt ?? latest.OccurredAt))) {
      latest = ev
    }
  }

  return latest
}

function resolveRhythmMode(pepHealthCheckDisplay) {
  const onboardingState = pepHealthCheckDisplay?.onboardingState
  const futureState = pepHealthCheckDisplay?.futureState

  if (onboardingState === 'Pending' || onboardingState == null) {
    return PEP_DATA_RHYTHM_MODES.onboarding
  }

  if (futureState === 'Active') {
    return PEP_DATA_RHYTHM_MODES.ongoing
  }

  return onboardingState === 'Done'
    ? PEP_DATA_RHYTHM_MODES.ongoing
    : PEP_DATA_RHYTHM_MODES.onboarding
}

function getScheduleForMode(modeKey) {
  if (modeKey === PEP_DATA_RHYTHM_MODES.onboarding) {
    return {
      modeKey,
      modeLabel: 'Onboarding (historical catch-up)',
      reservationPullDescription: 'about every 18 minutes',
      reservationPullIntervalMinutes: ONBOARDING_PULL_INTERVAL_MINUTES,
      heartbeatDescription: 'about every 5 minutes while the app is open',
      heartbeatIntervalMinutes: HEARTBEAT_INTERVAL_MINUTES
    }
  }

  return {
    modeKey: PEP_DATA_RHYTHM_MODES.ongoing,
    modeLabel: 'Ongoing (future reservations)',
    reservationPullDescription: 'about every 2 hours',
    reservationPullIntervalMinutes: ONGOING_PULL_INTERVAL_MINUTES,
    heartbeatDescription: 'about every 5 minutes while the app is open',
    heartbeatIntervalMinutes: HEARTBEAT_INTERVAL_MINUTES
  }
}

/**
 * Builds a CSM-facing summary of expected PEP desktop data pull cadence and current freshness.
 *
 * @param {{
 *   timelineEvents?: object[],
 *   lastActivityAt?: string | null,
 *   pepHealthCheckDisplay?: object | null,
 *   hotelTimeZoneId?: string | null,
 *   now?: moment.Moment
 * }} input
 */
export function derivePepDataRhythm({
  timelineEvents = [],
  lastActivityAt = null,
  pepHealthCheckDisplay = null,
  hotelTimeZoneId = null,
  now = moment.utc()
}) {
  const schedule = getScheduleForMode(resolveRhythmMode(pepHealthCheckDisplay))
  const latestPullEvent = findLatestDataPullEvent(timelineEvents)
  const lastPullAt = latestPullEvent?.occurredAt ?? latestPullEvent?.OccurredAt ?? null
  const lastAppSignalAt = lastActivityAt || null

  const minutesSincePull = lastPullAt ? now.diff(moment.utc(lastPullAt), 'minutes', true) : null
  const minutesSinceSignal = lastAppSignalAt
    ? now.diff(moment.utc(lastAppSignalAt), 'minutes', true)
    : null

  const expectedPullMinutes = schedule.reservationPullIntervalMinutes
  const overdueThresholdMinutes = Math.round(expectedPullMinutes * 1.5)

  let statusKey = PEP_DATA_RHYTHM_STATUS.waiting
  let severity = 'info'
  let statusLabel = 'Waiting for data'
  let statusDetail =
    'The desktop app pulls reservation data automatically while it is open on a connected PC.'

  if (minutesSinceSignal == null || minutesSinceSignal >= NO_APP_SIGNAL_MINUTES) {
    statusKey = PEP_DATA_RHYTHM_STATUS.noAppSignal
    severity = 'warn'
    statusLabel = 'No recent app signal'
    statusDetail =
      'No desktop telemetry has arrived in the last 24 hours. The PC may be off, asleep, offline, or the Directful desktop app may be closed. Reservation pulls only run while the app is open.'
  } else if (!lastPullAt) {
    statusKey = PEP_DATA_RHYTHM_STATUS.noPullYet
    severity = 'warn'
    statusLabel = 'No successful data pull yet'
    statusDetail =
      'The app has sent telemetry recently, but no successful reservation data pull appears in the loaded window. Confirm Hilton PEP sign-in is complete and leave the app running.'
  } else if (minutesSincePull <= expectedPullMinutes) {
    statusKey = PEP_DATA_RHYTHM_STATUS.normal
    severity = 'success'
    statusLabel = 'Normal'
    statusDetail = `Last reservation data pull is within the expected ${schedule.reservationPullDescription} window.`
  } else if (minutesSincePull <= overdueThresholdMinutes) {
    statusKey = PEP_DATA_RHYTHM_STATUS.waiting
    severity = 'info'
    statusLabel = 'Waiting for next pull'
    statusDetail = `The last pull was ${Math.round(minutesSincePull)} minutes ago. During ${schedule.modeLabel.toLowerCase()}, a new pull is expected ${schedule.reservationPullDescription}.`
  } else if (minutesSinceSignal <= RECENT_APP_SIGNAL_MINUTES) {
    statusKey = PEP_DATA_RHYTHM_STATUS.pullOverdue
    severity = 'warn'
    statusLabel = 'Data pull overdue'
    statusDetail =
      'The desktop app was active recently, but reservation data has not refreshed within the expected interval. Ask the hotel to keep the app open and signed in, or escalate if this persists.'
  } else {
    statusKey = PEP_DATA_RHYTHM_STATUS.pullOverdue
    severity = 'warn'
    statusLabel = 'Data may be stale'
    statusDetail =
      'Reservation data has not refreshed recently and the app signal is aging. Confirm the PC is on and the Directful desktop app is running.'
  }

  return {
    schedule,
    lastPullAt,
    lastPullLabel: formatHotelTime(lastPullAt, hotelTimeZoneId, { includeZoneLabel: true }),
    lastAppSignalAt,
    lastAppSignalLabel: formatHotelTime(lastAppSignalAt, hotelTimeZoneId, {
      includeZoneLabel: true
    }),
    minutesSincePull: minutesSincePull == null ? null : Math.round(minutesSincePull),
    minutesSinceSignal: minutesSinceSignal == null ? null : Math.round(minutesSinceSignal),
    statusKey,
    statusLabel,
    statusDetail,
    severity,
    automaticTasks: [
      {
        label: 'Reservation data pull',
        frequency: schedule.reservationPullDescription
      },
      {
        label: 'App check-in signal',
        frequency: schedule.heartbeatDescription
      }
    ]
  }
}

/**
 * CSM-facing info bar copy under Guest data uploads.
 * @param {ReturnType<typeof derivePepDataRhythm>} rhythm
 */
export function buildPepDataRhythmInfoBar(rhythm) {
  if (!rhythm) {
    return {
      title: '',
      body: ''
    }
  }

  const frequency = rhythm.schedule.reservationPullDescription
  const scheduleIntro = `Guest reservations are pulled from PEP automatically ${frequency} while the Directful desktop app is open on a connected PC.`

  const lastPullSentence = rhythm.lastPullAt
    ? `Last successful pull: ${rhythm.lastPullLabel}.`
    : 'No successful reservation pull appears in the loaded telemetry window yet.'

  let statusSentence = rhythm.statusDetail

  switch (rhythm.statusKey) {
    case PEP_DATA_RHYTHM_STATUS.normal:
      statusSentence = 'Reservation data is up to date for the expected pull schedule.'
      break
    case PEP_DATA_RHYTHM_STATUS.waiting:
      statusSentence =
        rhythm.minutesSincePull != null
          ? `The last pull was ${rhythm.minutesSincePull} minutes ago. A new pull is expected ${frequency}.`
          : `A new pull is expected ${frequency}.`
      break
    case PEP_DATA_RHYTHM_STATUS.noPullYet:
      statusSentence =
        'The app has sent telemetry recently, but no successful pull is visible yet. Confirm Hilton PEP sign-in and keep the app running.'
      break
    case PEP_DATA_RHYTHM_STATUS.noAppSignal:
      statusSentence =
        'No desktop app signal in the last 24 hours. Pulls only run while the app is open on an online PC.'
      break
    case PEP_DATA_RHYTHM_STATUS.pullOverdue:
      statusSentence =
        rhythm.minutesSincePull != null
          ? `The last pull was ${rhythm.minutesSincePull} minutes ago, which is longer than the expected ${frequency} interval. Ask the hotel to keep the app open and signed in.`
          : `Reservation data has not refreshed within the expected ${frequency} interval. Ask the hotel to keep the app open and signed in.`
      break
    default:
      break
  }

  return {
    title: rhythm.statusLabel,
    body: `${scheduleIntro} ${lastPullSentence} ${statusSentence}`.replace(/\s+/g, ' ').trim()
  }
}
